'use client';

import { Alert, Button, Stack, Typography } from '@mui/material';
import type {
  CheckCdcPrerequisitesResponse,
  ParameterCheckResult,
  SourceSizeCheckResult,
} from '@repo/api-gw-sdk';
import { PrivateLinkCheckResultStatusEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { Loader } from '@/components/layout/loading';
import { Panels } from '@/components/panels/panels';
import { ContactSupportLink } from '@/components/support/support';
import { useViewParameters } from '@/contexts/navigation';
import { useDAL } from '@/contexts/useDAL';
import { useUser } from '@/contexts/useUser';
import { useWorkspace } from '@/contexts/useWorkspace';
import { PanelSize } from '@/contexts/workspaceContext';
import { PreferencesKey } from '@/data/dal/preferencesKey';

import { CheckResultRow } from './rds-cdc-wizard/CheckResultRow';

interface RdsCdcSectionProps {
  resourceId: string;
}

const PrivateLinkStatusRow = ({
  checks,
  isPrivateLinkActive,
  privateLinkStatus,
}: {
  checks: CheckCdcPrerequisitesResponse;
  isPrivateLinkActive: boolean;
  privateLinkStatus: PrivateLinkCheckResultStatusEnum | undefined;
}) => (
  <CheckResultRow
    status={
      isPrivateLinkActive
        ? 'ok'
        : privateLinkStatus === PrivateLinkCheckResultStatusEnum.InProgress ||
            privateLinkStatus === PrivateLinkCheckResultStatusEnum.TearingDown
          ? 'pending'
          : 'error'
    }
    label='AWS PrivateLink'
  >
    {privateLinkStatus === PrivateLinkCheckResultStatusEnum.Active && (
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ wordBreak: 'break-all' }}
      >
        {checks?.privateLinkCheck?.ramShareArn}
      </Typography>
    )}

    {privateLinkStatus === PrivateLinkCheckResultStatusEnum.InProgress && (
      <Typography variant='body2' color='text.secondary'>
        Setup in progress. This might take a few minutes.
      </Typography>
    )}

    {privateLinkStatus === PrivateLinkCheckResultStatusEnum.TearingDown && (
      <Stack gap='8px' direction='row' alignItems='center'>
        <Loader variant='inline' />
        <Typography variant='body2' color='text.secondary'>
          Teardown in progress. This might take a few minutes.
        </Typography>
      </Stack>
    )}

    {privateLinkStatus === PrivateLinkCheckResultStatusEnum.NotSetup && (
      <Typography variant='body2' color='text.secondary'>
        Not configured
      </Typography>
    )}
  </CheckResultRow>
);

const ParameterCheckRow = ({ param }: { param: ParameterCheckResult }) => (
  <CheckResultRow
    status={param.ok ? 'ok' : 'error'}
    label={`Database Parameter: ${param.parameterName}`}
  >
    {param.ok && (
      <Typography variant='body2' color='text.secondary'>
        Value: <code>{param.currentValue}</code> on parameter group{' '}
        <code>{param.parameterGroupName}</code>
      </Typography>
    )}
    {!param.ok && param.error && (
      <Alert severity='error' sx={{ mt: '4px' }}>
        {param.error}
      </Alert>
    )}
    {!param.ok && !param.error && (
      <Typography variant='body2' color='text.secondary'>
        Current: <code>{param.currentValue || '(default)'}</code>. Set to{' '}
        <code>{param.requiredValue}</code> in parameter group{' '}
        <code>{param.parameterGroupName}</code>
      </Typography>
    )}
  </CheckResultRow>
);

const CdcBackupModeRow = ({
  isCdcEnabled,
  isDevMode,
  isActionLoading,
  cdcLoading,
  handleToggleCdc,
}: {
  isCdcEnabled: boolean;
  isDevMode: boolean;
  isActionLoading: boolean;
  cdcLoading: boolean;
  handleToggleCdc: () => void;
}) => (
  <CheckResultRow
    status={isCdcEnabled ? 'ok' : 'error'}
    label='Snapshot Engine Status'
  >
    <Typography variant='body2' color='text.secondary'>
      {isCdcEnabled
        ? 'Eligible for CDC - resource will be backed up with CDC'
        : 'Not eligible for CDC. Complete setup to enable, or contact Eon support.'}
    </Typography>

    {isDevMode && (
      <Stack direction='row' gap='8px' alignItems='center' mt='4px'>
        <Button
          variant={isCdcEnabled ? 'outlined' : 'contained'}
          color={isCdcEnabled ? 'error' : 'primary'}
          size='small'
          onClick={handleToggleCdc}
          disabled={isActionLoading}
          startIcon={
            cdcLoading ? (
              <i className='line-md-loading-loop w-[16px] h-[16px]' />
            ) : undefined
          }
        >
          {cdcLoading
            ? 'Updating...'
            : isCdcEnabled
              ? 'Force Disable'
              : 'Force Enable'}
        </Button>
      </Stack>
    )}
  </CheckResultRow>
);

const SourceSizeCheckRow = ({ check }: { check: SourceSizeCheckResult }) => (
  <CheckResultRow status={check.ok ? 'ok' : 'error'} label='Database Size'>
    {check.ok && (
      <Typography variant='body2' color='text.secondary'>
        {check.sourceSizeGb?.toFixed(1)} GB (minimum:{' '}
        {check.minimumSizeGb?.toFixed(1)} GB)
      </Typography>
    )}
    {!check.ok && (
      <Typography variant='body2' color='error'>
        {check.error || (
          <>
            Database size does not meet the minimum requirement for CDC. Please{' '}
            <ContactSupportLink /> for assistance.
          </>
        )}
      </Typography>
    )}
  </CheckResultRow>
);

export const RdsCdcSection = ({ resourceId }: RdsCdcSectionProps) => {
  const dal = useDAL();
  const { isEmployee } = useUser();
  const { rightPanel } = useWorkspace();
  const { removeParam } = useViewParameters();
  const { data: prefResponse } = dal.preferences.useUserPref(
    PreferencesKey.Preferences
  );
  const isDevMode = isEmployee && (prefResponse?.value?.isDevMode ?? false);

  const {
    data: checks,
    isLoading: checksLoading,
    error: checksError,
    mutate: runChecks,
  } = dal.inventory.privateLink.useCheckCdcPrerequisites(resourceId, {
    refreshInterval: (data) => {
      const status = data?.privateLinkCheck?.status;
      return status === PrivateLinkCheckResultStatusEnum.InProgress ||
        status === PrivateLinkCheckResultStatusEnum.TearingDown
        ? 5000
        : 0;
    },
  });

  const [teardownLoading, setTeardownLoading] = useState(false);
  const [cdcLoading, setCdcLoading] = useState(false);
  const [result, setResult] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const privateLinkStatus = checks?.privateLinkCheck?.status;
  const isPrivateLinkActive =
    privateLinkStatus === PrivateLinkCheckResultStatusEnum.Active;
  const isPrivateLinkConfigured =
    privateLinkStatus !== undefined &&
    privateLinkStatus !== PrivateLinkCheckResultStatusEnum.NotSetup;
  const isCdcEnabled = checks?.cdcBackupModeCheck?.enabled ?? false;
  const isSourceSizeTooSmall =
    checks?.sourceSizeCheck?.hasMinimum === true &&
    !checks?.sourceSizeCheck?.ok;

  const isActionLoading = teardownLoading || cdcLoading;

  const onClose = () => {
    rightPanel.setIsOpen(false);
    removeParam('id');
  };

  const goBackToInstancePanel = () => {
    rightPanel.setComponent({
      panel: Panels.InventoryInstance,
      props: {
        id: resourceId,
        onClose,
        initialTab: 'overview',
      },
    });
    rightPanel.setIsOpen(true, PanelSize.Large);
  };

  const openSetupWizard = () => {
    rightPanel.setComponent({
      panel: Panels.RdsCdcWizard,
      props: {
        resourceId,
        onAbort: goBackToInstancePanel,
        onFinished: () => {
          goBackToInstancePanel();
          void runChecks();
        },
      },
    });
    rightPanel.setIsOpen(true, PanelSize.Full);
  };

  const handleTeardown = () => {
    setTeardownLoading(true);
    setResult(null);

    dal.inventory.privateLink
      .teardownAwsPrivateLink(resourceId)
      .then((response) => {
        setResult({
          type: 'success',
          message: `PrivateLink teardown started. Workflow ID: ${response.workflowId}`,
        });
        void runChecks();
      })
      .catch((err: unknown) => {
        setResult({
          type: 'error',
          message:
            err instanceof Error
              ? err.message
              : 'Failed to teardown PrivateLink',
        });
      })
      .finally(() => setTeardownLoading(false));
  };

  const handleToggleCdc = () => {
    setCdcLoading(true);
    setResult(null);

    dal.inventory.configuration
      .update(resourceId, { setDbBackupModeCdc: !isCdcEnabled })
      .then(() => {
        setResult({
          type: 'success',
          message: isCdcEnabled
            ? 'CDC backup mode disabled'
            : 'CDC backup mode enabled — the CDC scheduler will pick up this resource',
        });
        void runChecks();
      })
      .catch((err: unknown) => {
        setResult({
          type: 'error',
          message:
            err instanceof Error
              ? err.message
              : 'Failed to update CDC backup mode',
        });
      })
      .finally(() => setCdcLoading(false));
  };

  if (checksLoading) {
    return (
      <Stack gap='16px'>
        <Typography variant='h4'>RDS CDC</Typography>
        <Stack direction='row' alignItems='center' gap='12px'>
          <Loader variant='inline' />
          <Typography variant='body2' color='text.secondary'>
            Checking status...
          </Typography>
        </Stack>
      </Stack>
    );
  }

  if (checksError) {
    return (
      <Stack gap='16px'>
        <Typography variant='h4'>RDS CDC</Typography>
        <Alert severity='error'>
          Failed to check CDC Status. Please try again or <ContactSupportLink />{' '}
          if the issue persists.
        </Alert>
        <Button
          variant='outlined'
          size='small'
          onClick={() => void runChecks()}
          sx={{ alignSelf: 'flex-start' }}
        >
          Retry
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap='16px'>
      <Typography variant='h4'>RDS CDC</Typography>

      {result && (
        <Alert severity={result.type} onClose={() => setResult(null)}>
          {result.message}
        </Alert>
      )}

      {(!isPrivateLinkConfigured || isDevMode) && (
        <Stack direction='row' gap='8px' alignItems='center'>
          {!isPrivateLinkConfigured && (
            <Button
              variant='outlined'
              size='small'
              onClick={openSetupWizard}
              disabled={isSourceSizeTooSmall}
            >
              Set up RDS CDC
            </Button>
          )}
          {isDevMode && isPrivateLinkConfigured && (
            <>
              <Button variant='outlined' size='small' onClick={openSetupWizard}>
                Set up RDS CDC Again
              </Button>
              <Button
                variant='outlined'
                color='error'
                size='small'
                onClick={handleTeardown}
                disabled={isActionLoading}
                startIcon={
                  teardownLoading ? (
                    <i className='line-md-loading-loop w-[16px] h-[16px]' />
                  ) : undefined
                }
              >
                {teardownLoading
                  ? 'Tearing down...'
                  : privateLinkStatus ===
                      PrivateLinkCheckResultStatusEnum.TearingDown
                    ? 'Retry Teardown'
                    : 'Teardown PrivateLink'}
              </Button>
            </>
          )}
        </Stack>
      )}

      <PrivateLinkStatusRow
        checks={checks!}
        isPrivateLinkActive={isPrivateLinkActive}
        privateLinkStatus={privateLinkStatus}
      />

      {checks?.parameterChecks?.map((param) => (
        <ParameterCheckRow key={param.parameterName} param={param} />
      ))}

      {checks?.sourceSizeCheck?.hasMinimum && (
        <SourceSizeCheckRow check={checks.sourceSizeCheck} />
      )}

      <CdcBackupModeRow
        isCdcEnabled={isCdcEnabled}
        isDevMode={isDevMode}
        isActionLoading={isActionLoading}
        cdcLoading={cdcLoading}
        handleToggleCdc={handleToggleCdc}
      />
    </Stack>
  );
};
